import React, { useState } from 'react';
import { useResponseMessages } from '../../hooks/useResponseMessage';
import EngagementMessage from '../ui/EngagementMessage';
import { EngagementMessageService } from '../../services/engagement-message.service';
import { formatCurrency } from '../../utilities/general-utils';

/**
 * Test component to preview engagement messages
 * Use this to test different scenarios and message types
 */
const EngagementMessageTester = () => {
    const { addMessage } = useResponseMessages();
    const [currentMessage, setCurrentMessage] = useState(null);

    // Mock data for testing different scenarios
    const testScenarios = {
        debtPayoff: {
            name: 'Credit Card Paid Off',
            data: {
                accountId: '1',
                accountName: 'Chase Freedom Credit Card',
                amount: 4250.75,
                remainingDebts: 2,
                interestSaved: 890.50
            }
        },
        finalDebtPayoff: {
            name: 'DEBT FREE - Final Payment',
            data: {
                accountId: '3',
                accountName: 'Car Loan',
                amount: 1850.00,
                remainingDebts: 0,
                interestSaved: 2400.00,
                totalDebtPaid: 45000,
                monthsEarly: 8
            }
        },
        budgetWarning: {
            name: 'Budget Minimum Reached',
            data: {
                type: 'minimum_reached',
                minimumAmount: 1000,
                currentBalance: 1000
            }
        },
        budgetSuccess: {
            name: 'Positive Cash Flow',
            data: {
                type: 'positive_flow',
                surplus: 450.25,
                improvement: 780.50
            }
        },
        halfwayMilestone: {
            name: 'Halfway to Debt Free',
            data: {
                milestone: 'halfway',
                remaining: 12500.75,
                percentComplete: 50.2
            }
        },
        aheadOfSchedule: {
            name: 'Ahead of Schedule',
            data: {
                milestone: 'month_ahead',
                monthsAhead: 3
            }
        },
        streak7Days: {
            name: '7-Day Payment Streak',
            data: {
                days: 7,
                payments: 7
            }
        },
        streak30Days: {
            name: '30-Day Payment Streak',
            data: {
                days: 30,
                payments: 28
            }
        },
        encouragement: {
            name: 'Encouragement Message',
            data: {
                strugglingIndicators: ['missed_payments', 'negative_cash_flow']
            }
        }
    };

    const generateTestMessage = (scenarioKey) => {
        const scenario = testScenarios[scenarioKey];
        let message = null;

        switch (scenarioKey) {
            case 'debtPayoff':
                message = EngagementMessageService.createDebtPayoffMessage(scenario.data);
                break;
            case 'finalDebtPayoff':
                // First show the regular payoff message
                const payoffMessage = EngagementMessageService.createDebtPayoffMessage(scenario.data);
                setCurrentMessage(payoffMessage);
                
                // Then show the debt-free celebration after 3 seconds
                setTimeout(() => {
                    const debtFreeMessage = EngagementMessageService.createDebtFreeMessage({
                        totalDebtPaid: scenario.data.totalDebtPaid,
                        interestSaved: scenario.data.interestSaved,
                        monthsEarly: scenario.data.monthsEarly
                    });
                    setCurrentMessage(debtFreeMessage);
                }, 3000);
                return;
            case 'budgetWarning':
            case 'budgetSuccess':
                message = EngagementMessageService.createBudgetStateMessage(scenario.data);
                break;
            case 'halfwayMilestone':
            case 'aheadOfSchedule':
                message = EngagementMessageService.createProgressMessage(scenario.data);
                break;
            case 'streak7Days':
            case 'streak30Days':
                message = EngagementMessageService.createStreakMessage(scenario.data);
                break;
            case 'encouragement':
                message = EngagementMessageService.generateEncouragementMessage(scenario.data);
                break;
            default:
                return;
        }

        if (message) {
            setCurrentMessage(message);
        }
    };

    const handleAction = (action, messageData) => {
        console.log('Action clicked:', action, messageData);
        alert(`Action: ${action.label}\nData: ${JSON.stringify(messageData.data, null, 2)}`);
    };

    const handleDismiss = () => {
        setCurrentMessage(null);
    };

    const addToResponseSystem = (scenarioKey) => {
        const scenario = testScenarios[scenarioKey];
        
        // Add to your existing response message system for comparison
        switch (scenarioKey) {
            case 'debtPayoff':
                addMessage({
                    type: 'notification',
                    data: `🎉 ${scenario.data.accountName} paid off! ${formatCurrency(scenario.data.amount)} debt eliminated!`
                });
                break;
            case 'budgetWarning':
                addMessage({
                    type: 'warning',
                    data: `⚠️ Budget alert: You've reached your minimum balance of ${formatCurrency(scenario.data.minimumAmount)}`
                });
                break;
            case 'budgetSuccess':
                addMessage({
                    type: 'notification', 
                    data: `📈 Great job! You have ${formatCurrency(scenario.data.surplus)} positive cash flow this month!`
                });
                break;
            default:
                addMessage({
                    type: 'notification',
                    data: `✨ ${scenario.name} - Test message from engagement system`
                });
        }
    };

    return (
        <div style={{ 
            padding: '2rem', 
            maxWidth: '800px', 
            margin: '0 auto',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            <h2 style={{ marginBottom: '2rem', color: '#333' }}>
                🧪 Engagement Message Tester
            </h2>
            
            <div style={{ marginBottom: '3rem' }}>
                <h3 style={{ marginBottom: '1rem', color: '#555' }}>Test Scenarios:</h3>
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '1rem',
                    marginBottom: '2rem'
                }}>
                    {Object.entries(testScenarios).map(([key, scenario]) => (
                        <div key={key} style={{
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '1rem'
                        }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
                                {scenario.name}
                            </h4>
                            <div style={{ 
                                display: 'flex', 
                                gap: '0.5rem', 
                                flexWrap: 'wrap'
                            }}>
                                <button
                                    onClick={() => generateTestMessage(key)}
                                    style={{
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    Test Enhanced
                                </button>
                                <button
                                    onClick={() => addToResponseSystem(key)}
                                    style={{
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    Test Current
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '1rem', 
                    borderRadius: '6px',
                    marginBottom: '2rem'
                }}>
                    <h4 style={{ margin: '0 0 0.5rem 0' }}>Usage Instructions:</h4>
                    <ul style={{ margin: '0', paddingLeft: '1.5rem' }}>
                        <li><strong>Test Enhanced:</strong> Shows the new engagement message system</li>
                        <li><strong>Test Current:</strong> Shows your existing response message system for comparison</li>
                        <li><strong>Final Debt Payoff:</strong> Shows a sequence - regular payoff followed by debt-free celebration</li>
                        <li>Check your browser console for action click logging</li>
                    </ul>
                </div>
            </div>

            {/* Enhanced Message Display Area */}
            <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '1rem', color: '#555' }}>Enhanced Message Preview:</h3>
                <div style={{ 
                    minHeight: '100px',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    padding: '1rem',
                    backgroundColor: '#fafafa'
                }}>
                    {currentMessage ? (
                        <EngagementMessage 
                            messageData={currentMessage}
                            onAction={handleAction}
                            onDismiss={handleDismiss}
                        />
                    ) : (
                        <p style={{ 
                            textAlign: 'center', 
                            color: '#666',
                            margin: '2rem 0',
                            fontStyle: 'italic'
                        }}>
                            Click "Test Enhanced" on any scenario above to preview the engagement message here
                        </p>
                    )}
                </div>
            </div>

            {/* Quick Actions */}
            <div style={{
                backgroundColor: '#e3f2fd',
                padding: '1.5rem',
                borderRadius: '8px',
                marginTop: '2rem'
            }}>
                <h4 style={{ margin: '0 0 1rem 0' }}>Quick Test Actions:</h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => {
                            generateTestMessage('debtPayoff');
                            setTimeout(() => generateTestMessage('halfwayMilestone'), 4000);
                            setTimeout(() => generateTestMessage('streak30Days'), 8000);
                        }}
                        style={{
                            padding: '0.75rem 1rem',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        🎉 Success Sequence
                    </button>
                    <button
                        onClick={() => {
                            generateTestMessage('budgetWarning');
                            setTimeout(() => generateTestMessage('encouragement'), 3000);
                        }}
                        style={{
                            padding: '0.75rem 1rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        ⚠️ Warning Sequence
                    </button>
                    <button
                        onClick={() => setCurrentMessage(null)}
                        style={{
                            padding: '0.75rem 1rem',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        Clear Messages
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EngagementMessageTester;